import { getLimitsForTier } from "@/lib/cat/usageService";

export type PlanId = "free" | "standard" | "premium";
export type PaidPlanId = Exclude<PlanId, "free">;
export type TokenPackKey = "starter" | "standard";

// Plan ids and prices must match SUBSCRIPTION_PLANS and TOKEN_PACKS in
// translyx-api (src/routes/cat-payfast.js). PayFast bills in ZAR only.
export const BILLING_PLANS: {
    id: PlanId;
    name: string;
    priceZar: number;
    // Indicative USD price, shown first where we quote prices to visitors.
    priceUsd: number;
    badge: string | null;
}[] = [
    { id: "free", name: "Free", priceZar: 0, priceUsd: 0, badge: null },
    { id: "standard", name: "Standard", priceZar: 165, priceUsd: 10, badge: null },
    { id: "premium", name: "Premium", priceZar: 450, priceUsd: 27, badge: "Best value" },
];

export const TOKEN_PACK_PRICES_ZAR: Record<TokenPackKey, number> = {
    starter: 75,
    standard: 270,
};

// Token packs must match TOKEN_PACKS in translyx-api (cat-payfast.js).
export const TOKEN_PACKS: { key: TokenPackKey; name: string; chars: number }[] = [
    { key: "starter", name: "Starter", chars: 100000 },
    { key: "standard", name: "Standard", chars: 500000 },
];

export const PAID_PLAN_IDS: PaidPlanId[] = ["standard", "premium"];

export function isPaidPlanId(value: string | null | undefined): value is PaidPlanId {
    return !!value && (PAID_PLAN_IDS as string[]).includes(value);
}

export function planRank(id: string | null | undefined): number {
    return BILLING_PLANS.findIndex((p) => p.id === id);
}

export function formatZar(amount: number): string {
    return amount === 0 ? "R0" : `R${amount.toFixed(2).replace(/\.00$/, "")}`;
}

export function formatCount(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return `${n}`;
}

export function planLimits(id: PlanId) {
    return getLimitsForTier(id);
}

export const PENDING_PLAN_KEY = "pending_plan";

// The plan a visitor chose before signing up or logging in. sessionStorage can
// be unavailable (private mode, blocked storage), so every access is guarded.
export function setPendingPlan(plan: PaidPlanId): void {
    try {
        sessionStorage.setItem(PENDING_PLAN_KEY, plan);
    } catch {
        /* ignore */
    }
}

export function peekPendingPlan(): PaidPlanId | null {
    try {
        const value = sessionStorage.getItem(PENDING_PLAN_KEY);
        return isPaidPlanId(value) ? value : null;
    } catch {
        return null;
    }
}

export function clearPendingPlan(): void {
    try {
        sessionStorage.removeItem(PENDING_PLAN_KEY);
    } catch {
        /* ignore */
    }
}

export function billingPath(plan: PaidPlanId): string {
    return `/billing?plan=${plan}`;
}

// Tells the rest of the app (the sidebar badge, for one) that the plan may have
// changed, so it reads it again instead of showing a stale tier.
export const TIER_CHANGED_EVENT = "cat:tier-changed";

export function notifyTierChanged(): void {
    window.dispatchEvent(new Event(TIER_CHANGED_EVENT));
}
